import type { ReactNode } from 'react';
import { useEffect, useEffectEvent, useId, useMemo, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Paper } from '@mui/material';

import Typography from '@/components/Typography';
import { useOptionalAccordion } from '@/features/DataHub/components/ExportDrawer/AccordionContext';

import type { SxProps, Theme } from '@mui/material/styles';

const headerContentSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 2,
  flex: 1
};

const titleBoxSx: SxProps<Theme> = { display: 'flex', flexDirection: 'column', gap: 1 };

const titleTypographySx: SxProps<Theme> = {
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  gap: 0.5
};

const collapsedValueSx: SxProps<Theme> = { fontWeight: 400 };

const accordionSx: SxProps<Theme> = {
  p: 0,
  py: '8px',
  flex: 1,
  '&:before': { display: 'none' },
  boxShadow: 'none'
};

const accordionDetailsSx: SxProps<Theme> = { pt: 0 };

const subtitleSx: SxProps<Theme> = { fontWeight: 500, color: 'text.subtext', mb: '8px' };

const paperLightSx: SxProps<Theme> = {
  p: 2,
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'background.paper'
};

const paperDarkSx: SxProps<Theme> = {
  p: 2,
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'background.default'
};

const headerWithChildrenSx: SxProps<Theme> = { mb: 2 };
const headerNoChildrenSx: SxProps<Theme> = { mb: 0 };

export const DataHubCard = ({
  children,
  title,
  subtitle,
  renderRight,
  icon,
  expandable,
  defaultExpanded = false,
  collapsedSummary,
  darkBg = false
}: {
  children?: ReactNode;
  title: ReactNode;
  subtitle?: string;
  renderRight?: ReactNode;
  icon?: ReactNode;
  expandable?: boolean;
  defaultExpanded?: boolean;
  collapsedSummary?: ReactNode;
  darkBg?: boolean;
}) => {
  const id = useId();
  const accordion = useOptionalAccordion();
  const [localExpandedId, setLocalExpandedId] = useState<string | null>(null);
  const expandedId = accordion?.expandedId ?? localExpandedId;
  const setExpandedId = accordion?.setExpandedId ?? setLocalExpandedId;
  const expanded = useMemo(() => expandedId === id, [expandedId, id]);

  const header = (
    <Box sx={headerContentSx}>
      <Box sx={titleBoxSx}>
        <Typography
          component='div'
          size={16}
          sx={titleTypographySx}
        >
          {icon} {title}
          {!expanded && collapsedSummary != null && (
            <>
              :
              <Box
                component='span'
                sx={collapsedValueSx}
              >
                {collapsedSummary}
              </Box>
            </>
          )}
        </Typography>
      </Box>
      {renderRight && <Box>{renderRight}</Box>}
    </Box>
  );

  const checkForDefaultExpanded = useEffectEvent(() => {
    if (defaultExpanded) {
      setExpandedId(id);
    }
  });

  useEffect(() => {
    checkForDefaultExpanded();
  }, []);

  if (expandable) {
    return (
      <Accordion
        disableGutters
        expanded={expanded}
        sx={accordionSx}
        onChange={() => {
          setExpandedId(prevId => (prevId === id ? null : id));
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>{header}</AccordionSummary>
        <AccordionDetails sx={accordionDetailsSx}>
          {subtitle && (
            <Typography
              size={12}
              sx={subtitleSx}
            >
              {subtitle}
            </Typography>
          )}
          {children}
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <Paper sx={darkBg ? paperDarkSx : paperLightSx}>
      <Box sx={children ? headerWithChildrenSx : headerNoChildrenSx}>{header}</Box>
      {subtitle && (
        <Typography
          size={12}
          sx={subtitleSx}
        >
          {subtitle}
        </Typography>
      )}
      {children}
    </Paper>
  );
};
